/**
 * ServiceAccessPage - Admin page to grant and revoke access to the Service area
 * of an EXTERNAL box (MasterPower binding, relay switches, permissions, factory reset).
 *
 * Each grant is a 4-digit PIN stored in config/mobile, which every device reads and
 * caches. The PIN is 4 digits and nothing else because it is typed on a numeric keypad
 * on the box itself — unlike a beta invite code, which is read once on the web.
 */

import { useEffect, useState } from "react";
import {
  Card,
  Input,
  Button,
  Typography,
  Space,
  App,
  Skeleton,
  Table,
  Tag,
} from "antd";
import { getMobileConfig, updateMobileConfig } from "@/services/configService";

const { Title, Text } = Typography;

/**
 * Never handed out as someone's own: it is the PIN compiled into the app, the way in
 * when this list cannot be reached at all (a bench box, offline, not yet paired).
 */
const MASTER_PIN = "4321";

export default function ServiceAccessPage() {
  const [loading, setLoading] = useState(true);
  const [servicePins, setServicePins] = useState<Record<string, string>>({});
  const [newHolder, setNewHolder] = useState("");
  const [saving, setSaving] = useState(false);
  const { modal, message } = App.useApp();

  useEffect(() => {
    loadPins();
  }, []);

  const loadPins = async () => {
    try {
      setLoading(true);
      const data = await getMobileConfig();
      setServicePins(data.servicePins ?? {});
    } catch (error) {
      console.error("Failed to load service PINs:", error);
      message.error("Failed to load service access list");
    } finally {
      setLoading(false);
    }
  };

  /**
   * A 4-digit PIN that is free and is not the master one.
   *
   * 10 000 combinations means collisions become likely enough to check for once a
   * handful are issued, so the caller retries rather than trusting a single draw.
   */
  const generatePin = (taken: Record<string, string>): string | null => {
    for (let attempt = 0; attempt < 200; attempt++) {
      const pin = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
      if (pin !== MASTER_PIN && !(pin in taken)) return pin;
    }
    return null;
  };

  const savePins = async (pins: Record<string, string>, note: string) => {
    setSaving(true);
    try {
      await updateMobileConfig({ servicePins: pins });
      setServicePins(pins);
      message.success(note);
    } catch (error) {
      console.error("Failed to save service PINs:", error);
      message.error("Failed to save. Make sure you have admin privileges.");
    } finally {
      setSaving(false);
    }
  };

  const handleGrantAccess = async () => {
    const holder = newHolder.trim();
    if (!holder) {
      message.warning("Enter who this PIN is for");
      return;
    }
    const pin = generatePin(servicePins);
    if (!pin) {
      message.error("Could not find a free PIN — revoke some first");
      return;
    }
    await savePins({ ...servicePins, [pin]: holder }, `PIN ${pin} granted to ${holder}`);
    setNewHolder("");
  };

  const handleRevokeAccess = (pin: string, holder: string) => {
    modal.confirm({
      title: `Revoke access for ${holder}?`,
      content: `PIN ${pin} will stop working. Devices pick this up the next time they reach the network; one that is offline keeps accepting it until then.`,
      okText: "Revoke",
      okType: "danger",
      onOk: async () => {
        const next = { ...servicePins };
        delete next[pin];
        await savePins(next, `Access revoked for ${holder}`);
      },
    });
  };

  if (loading) {
    return <Skeleton active title={false} paragraph={{ rows: 6 }} />;
  }

  const rows = Object.entries(servicePins).map(([pin, holder]) => ({ pin, holder }));

  return (
    <div>
      <Title level={3}>Service Access</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Who can open the Service area on an EXTERNAL box — MasterPower binding, relay
        switches, permissions and factory reset. Grant a PIN per person; revoke it and it
        stops working as soon as their device reaches the network.
      </Text>

      <Card>
        <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
          <Input
            placeholder="Who is this PIN for (e.g. Mario Rossi — beta)"
            value={newHolder}
            onChange={(e) => setNewHolder(e.target.value)}
            onPressEnter={handleGrantAccess}
            disabled={saving}
          />
          <Button type="primary" onClick={handleGrantAccess} loading={saving}>
            Grant access
          </Button>
        </Space.Compact>

        <Table
          size="small"
          rowKey="pin"
          pagination={false}
          dataSource={rows}
          locale={{ emptyText: "No one has been granted access" }}
          columns={[
            {
              title: "PIN",
              dataIndex: "pin",
              width: 110,
              render: (pin: string) => (
                <Tag style={{ fontFamily: "monospace", fontSize: 14 }}>{pin}</Tag>
              ),
            },
            { title: "Granted to", dataIndex: "holder" },
            {
              title: "",
              width: 90,
              render: (_: unknown, row: { pin: string; holder: string }) => (
                <Button
                  danger
                  size="small"
                  type="link"
                  disabled={saving}
                  onClick={() => handleRevokeAccess(row.pin, row.holder)}
                >
                  Revoke
                </Button>
              ),
            },
          ]}
        />

      </Card>
    </div>
  );
}
